import type { SidebarState } from 'contexts/sidebarStateContext';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import type { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestionSourceCategories } from 'types/suggestions/QuoteSuggestions';

/** Synthetic SharePoint rows use negative ids; preserved when merging API suggestions into Kit. */
export function isSyntheticSharepointSuggestion(
  s: GenericSuggestion
): s is LoadBuildingSuggestions {
  return s.pipeline === SuggestionPipelines.LoadBuilding && s.id < 0;
}

/** SharePoint workbook rows merged into Kit as LB suggestions (persisted or fail-open synthetic). */
export function isSharepointSuggestion(
  s: GenericSuggestion
): s is LoadBuildingSuggestions {
  if (s.pipeline !== SuggestionPipelines.LoadBuilding) {
    return false;
  }
  const loadBuilding = s as LoadBuildingSuggestions;
  if (loadBuilding.sourceCategory === SuggestionSourceCategories.Sharepoint) {
    return true;
  }
  if (
    loadBuilding.sharepointAgilePasteHint ||
    loadBuilding.sharepointWriteback
  ) {
    return true;
  }
  return isSyntheticSharepointSuggestion(s);
}

type SharepointCarouselState = Pick<
  SidebarState,
  'curSuggestionList' | 'clickedSuggestion'
>;

/**
 * Strip SharePoint cards and drop `clickedSuggestion` when it is one of them.
 *
 * Every path that removes SharePoint cards without rebuilding them goes through here:
 * filtering `curSuggestionList` alone leaves the load-building form on a workbook row the user
 * can no longer see. Non-SharePoint selections are preserved, same ownership rule as
 * {@link resolveSharepointClickedSuggestion} on the rebuild path.
 */
export function clearSharepointCarouselState<T extends SharepointCarouselState>(
  prev: T
): T {
  const { curSuggestionList, clickedSuggestion } = prev;

  const withoutSharepoint = curSuggestionList.filter(
    (s) => !isSharepointSuggestion(s)
  );
  const clearsSelection =
    clickedSuggestion != null && isSharepointSuggestion(clickedSuggestion);

  // Callers re-run on every debounced sync; keep the same object so a no-op strip does not
  // re-render every sidebar consumer.
  if (
    withoutSharepoint.length === curSuggestionList.length &&
    !clearsSelection
  ) {
    return prev;
  }

  return {
    ...prev,
    curSuggestionList: withoutSharepoint,
    clickedSuggestion: clearsSelection ? null : clickedSuggestion,
  };
}

/**
 * Re-resolve `clickedSuggestion` after SharePoint suggestion list rebuild.
 *
 * Non-SharePoint selections (email LB, quote, etc.) are preserved unchanged so Graph/EWA
 * debounce does not clear an in-progress form. SharePoint selections match by `id` first;
 * when a row graduates from fail-open synthetic (negative `id`) to persisted (positive `id`),
 * falls back to `sourceExternalID`. Only when the prior card omitted `sourceExternalID`
 * (worksheet unknown) do we match `sharepointStableSyntheticId` - never after a known
 * `sourceExternalID` miss, or a same-index row on another worksheet could be selected.
 */
export function resolveSharepointClickedSuggestion(
  prevClicked: GenericSuggestion | null | undefined,
  suggestions: LoadBuildingSuggestions[],
  prevSharepoint: LoadBuildingSuggestions[]
): GenericSuggestion | null {
  if (prevClicked == null) {
    return prevSharepoint.length === 0 && suggestions.length > 0
      ? (suggestions[0] ?? null)
      : null;
  }

  // Email/quote/etc. - leave alone; SharePoint sync only owns SharePoint cards.
  if (!isSharepointSuggestion(prevClicked)) {
    return prevClicked;
  }

  const byId = suggestions.find((s) => s.id === prevClicked.id);
  if (byId) {
    return byId;
  }

  const prevSourceExternalId = prevClicked.sourceExternalID?.trim();
  if (prevSourceExternalId) {
    // Sheet/booking identity was known: miss means a different shipment set (e.g. sheet switch).
    return (
      suggestions.find(
        (s) => s.sourceExternalID?.trim() === prevSourceExternalId
      ) ?? null
    );
  }

  // Worksheet-unknown synthetics have no sourceExternalID; match the stable row fingerprint
  // that persists on both fail-open and persisted cards for the same workbook row.
  const prevStableId =
    prevClicked.sharepointStableSyntheticId ??
    (prevClicked.id < 0 ? prevClicked.id : undefined);
  if (prevStableId != null) {
    return (
      suggestions.find(
        (s) =>
          s.sharepointStableSyntheticId === prevStableId ||
          s.id === prevStableId
      ) ?? null
    );
  }

  return null;
}

function stabilityDateKey(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toISOString();
}

/**
 * Stable identity for bidding portal/carousel list comparisons.
 * Prefer portal load id when present. Chrome webpage scraping often
 * triggers a new update for the same load; treating those as new entries rewrites
 * state and can cascade into a remount loop.
 * When load id is missing (e.g. FreightView), fall back to route + shipment
 * fields for QQ (not volatile suggestion `id`) or suggestion id for other pipelines.
 */
export function getCarouselSuggestionStabilityKey(
  suggestion: GenericSuggestion
): string {
  const loadId = suggestion.sourceExternalID?.trim();

  if (loadId) {
    return `${suggestion.pipeline}:${loadId}`;
  }

  if (suggestion.pipeline === SuggestionPipelines.QuickQuote) {
    const { pipeline, suggested } = suggestion;
    const {
      mode,
      transportType,
      customerExternalTMSID,
      pickupCity,
      pickupState,
      pickupZip,
      pickupDate,
      deliveryCity,
      deliveryState,
      deliveryZip,
      deliveryDate,
    } = suggested;
    // FreightView scrapes often omit customerExternalTMSID; shipper name is the
    // identity signal in that case (see scrapedSuggestionUtil customer patching).
    const customerName =
      (suggested as { customer?: { name?: string } }).customer?.name?.trim() ??
      '';

    return [
      pipeline,
      mode,
      transportType,
      customerExternalTMSID,
      customerName,
      pickupCity,
      pickupState,
      pickupZip,
      stabilityDateKey(pickupDate),
      deliveryCity,
      deliveryState,
      deliveryZip,
      stabilityDateKey(deliveryDate),
    ].join(':');
  }

  return `${suggestion.pipeline}:${suggestion.id}`;
}

export function areCarouselSuggestionListsEquivalent(
  a: GenericSuggestion[],
  b: GenericSuggestion[]
): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((suggestion, index) => {
    const other = b[index];
    // SharePoint sync replaces cards under a stable sourceExternalID.
    // Object identity is the signal that context already has newer slides;
    // do not collapse those updates.
    if (isSharepointSuggestion(suggestion) || isSharepointSuggestion(other)) {
      return suggestion === other;
    }
    return (
      getCarouselSuggestionStabilityKey(suggestion) ===
      getCarouselSuggestionStabilityKey(other)
    );
  });
}
